import { palettes } from '@backstage/theme';
import { borderColor, rail, slate, status, surfaces, teal, text } from './tokens';

const statusPalette = (mode: 'light' | 'dark') => ({
  ...palettes[mode].status,
  ok: status.success.dot,
  warning: status.warning.dot,
  error: status.danger.dot,
  running: status.info.dot,
  pending: status.warning.dot,
  aborted: mode === 'light' ? slate[400] : slate[500],
});

/**
 * ClearSlate light palette — the canonical design-system theme.
 * Teal-700 spine, deep-petrol nav rail, pastel status, cool-slate neutrals.
 */
export const clrslateLightPalette = {
  ...palettes.light,
  // teal-700 fill takes white labels (5.5:1); teal-700 text reads on the light canvas (5.1:1).
  primary: { main: teal[700], light: teal[500], dark: teal[800], contrastText: text.onBrand },
  secondary: { main: slate[600], light: slate[400], dark: slate[800] },
  error: { main: status.danger.main },
  warning: { main: status.warning.main },
  success: { main: status.success.main },
  info: { main: status.info.main },
  background: { default: surfaces.canvas, paper: surfaces.surface },
  status: statusPalette('light'),
  border: surfaces.border,
  divider: borderColor.light,
  text: { primary: text.primary, secondary: text.muted },
  link: teal[700],
  linkHover: teal[600],
  navigation: {
    ...palettes.light.navigation,
    background: rail.base,
    indicator: rail.active,
    color: rail.text,
    selectedColor: text.onBrand,
    navItem: { hoverBackground: rail.hover },
    submenu: { ...palettes.light.navigation.submenu, background: teal[950] },
  },
};

/**
 * ClearSlate dark palette — a derived adaptation (the design system is
 * light-only). Brighter teal-500 spine over deep slate surfaces.
 */
export const clrslateDarkPalette = {
  ...palettes.dark,
  // Bright teal-500 so text/outlined button labels read on dark (7.2:1); contained
  // buttons use dark teal ink instead of white (white-on-teal-500 is only 2.5:1).
  primary: { main: teal[500], light: teal[400], dark: teal[700], contrastText: teal[950] },
  secondary: { main: slate[400], light: slate[300], dark: slate[600] },
  error: { main: status.danger.main },
  warning: { main: status.warning.main },
  success: { main: status.success.main },
  info: { main: status.info.main },
  background: { default: slate[900], paper: slate[800] },
  status: statusPalette('dark'),
  border: borderColor.dark,
  divider: borderColor.dark,
  text: { primary: '#e2e8f0', secondary: slate[400] },
  link: teal[400],
  linkHover: teal[300],
  navigation: {
    ...palettes.dark.navigation,
    background: '#08282c',
    indicator: teal[500],
    color: rail.text,
    selectedColor: text.onBrand,
    navItem: { hoverBackground: rail.hover },
    submenu: { ...palettes.dark.navigation.submenu, background: '#06201f' },
  },
};
